import React from "react";
import { AlertTriangle, X } from "lucide-react";
import type { BaseEntity, FieldValue, SimpleTableConfig } from "../types";
import { ActiveFilterChips } from "./advanced-filters/components/ActiveFilterChips";
import { FilterPanelControls } from "./AdvancedFilterPanel/FilterPanelControls";
import { renderFilterFieldInput } from "./AdvancedFilterPanel/FilterFieldInput";
import {
  collapsedFilterFields,
  filterHasValue,
  normalizeAdvancedFiltersForDisplay,
  type AdvancedFiltersProp,
} from "./AdvancedFilterPanel/filter-state";

/**
 * The advanced-filters panel body (also embedded in the filters popover).
 *
 * Filter-state shape + predicates live in `AdvancedFilterPanel/filter-state.ts`,
 * the per-field input precedence in `AdvancedFilterPanel/FilterFieldInput.tsx`,
 * and the operator/clear-all row in `AdvancedFilterPanel/FilterPanelControls.tsx`
 * (module-size lane L17). This module owns the layout.
 */
interface AdvancedFilterPanelProps<TData extends BaseEntity = BaseEntity> {
  showAdvancedFilters: boolean;
  config: SimpleTableConfig<TData>;
  advancedFilters: AdvancedFiltersProp;
  filterOperator: "AND" | "OR";
  setFilterOperator: (operator: "AND" | "OR") => void;
  addAdvancedFilter: (fieldId: string, filterType: string, value: FieldValue) => void;
  removeAdvancedFilter: (fieldId: string) => void;
  clearAllAdvancedFilters: () => void;
  /** When true, hides the panel's outer card chrome + title (for embedding in a popover) */
  hideHeader?: boolean;
}

export function AdvancedFilterPanel<TData extends BaseEntity = BaseEntity>({
  showAdvancedFilters,
  config,
  advancedFilters,
  filterOperator,
  setFilterOperator,
  addAdvancedFilter,
  removeAdvancedFilter,
  clearAllAdvancedFilters,
  hideHeader,
}: AdvancedFilterPanelProps<TData>) {
  if (!showAdvancedFilters) return null;

  const filtersRecord = normalizeAdvancedFiltersForDisplay(advancedFilters);

  // Active filters for chips + clear-all enablement.
  const activeFilters = Object.entries(filtersRecord).filter(([, filter]) =>
    filterHasValue(filter)
  );

  const filterableFields = Object.entries(config.fields).filter(
    ([, fieldConfig]) => fieldConfig.filterable && !fieldConfig.formOnly
  );

  // Fields the caller sent more than one condition for. The panel is one input
  // per field, so only the last is shown — say so and offer the way out rather
  // than letting the first condition keep filtering the data invisibly.
  const collapsed = collapsedFilterFields(advancedFilters);

  return (
    <div className={hideHeader ? "p-0" : "mb-4 rounded-lg border border-border bg-card p-4"}>
      <FilterPanelControls
        hideHeader={hideHeader}
        filterOperator={filterOperator}
        setFilterOperator={setFilterOperator}
        clearAllAdvancedFilters={clearAllAdvancedFilters}
        hasActiveFilters={activeFilters.length > 0}
      />

      {collapsed.length > 0 && (
        <div
          role="status"
          className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-700/60 dark:bg-amber-950/40 dark:text-amber-200"
        >
          <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
          <span>
            {collapsed
              .map((fieldId) => config.fields[fieldId]?.label || fieldId)
              .join(", ")}{" "}
            {collapsed.length === 1 ? "has" : "have"} more than one condition. This panel shows one
            per field, so only the last is editable here — the others are still being applied.
          </span>
          <button
            type="button"
            onClick={() => collapsed.forEach((fieldId) => removeAdvancedFilter(fieldId))}
            className="ml-auto inline-flex items-center gap-1 rounded-sm font-medium underline underline-offset-2 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="h-3 w-3" />
            Clear {collapsed.length === 1 ? "it" : "them"}
          </button>
        </div>
      )}

      {/* Active Filter Chips */}
      <ActiveFilterChips
        activeFilters={activeFilters}
        config={config}
        removeAdvancedFilter={removeAdvancedFilter}
      />

      {filterableFields.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">
          No filterable fields available.
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {filterableFields.map(([fieldId, fieldConfig]) => {
            const currentFilter = filtersRecord[fieldId];
            const isActive = filterHasValue(currentFilter);
            const input = renderFilterFieldInput({
              fieldId,
              fieldConfig,
              currentFilter,
              addAdvancedFilter,
              removeAdvancedFilter,
            });
            if (!input) return null;

            return (
              <div key={fieldId} className="flex flex-col gap-1.5">
                <div className="flex min-h-[20px] items-center justify-between gap-2">
                  <label className="text-xs font-medium text-muted-foreground">
                    {fieldConfig.label}
                  </label>
                  {isActive && (
                    <button
                      type="button"
                      onClick={() => removeAdvancedFilter(fieldId)}
                      aria-label={`Clear ${fieldConfig.label} filter`}
                      title={`Clear ${fieldConfig.label} filter`}
                      className="inline-flex items-center gap-0.5 text-[11px] font-medium text-muted-foreground transition-colors hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
                    >
                      <X className="h-3 w-3" />
                      Clear
                    </button>
                  )}
                </div>
                {input}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
